import { getNumberFormatter } from './get-number-formatter';

/**
 * PW-1638 — форматирование значений бонусного оффера для отображения.
 *
 * - валюта ПЕРЕД суммой с пробелом: `₱ 5,000`;
 * - дробная часть `.00` убирается, округление до целого по матем. правилам;
 * - пробел между числом и `FS`: `1FS` → `1 FS`.
 *
 * Глобальный `@/shared/libs/format-currency` намеренно не трогаем (он
 * используется по всему приложению и форматирует иначе — символ после суммы).
 */

const currencyAfterAmountPatterns = new Map<string, RegExp>();

const roundToInt = (amount: number | string): number => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return Number.isFinite(num) ? Math.round(num) : 0;
};

/** Деньги: `₱ 5,000` (символ перед суммой, целое, разряды через запятую). */
export const formatMoney = (
  amount: number | string,
  currencySymbol?: string
): string => {
  const value = getNumberFormatter('en-US').format(roundToInt(amount));
  return currencySymbol ? `${currencySymbol} ${value}` : value;
};

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Нормализует произвольную строку оффера (`text` опции / `value` условия):
 * убирает `.00`, ставит пробел перед `FS`, переносит символ валюты перед суммой.
 */
export const normalizeOfferText = (
  raw: unknown,
  currencySymbol?: string
): string => {
  if (raw == null) return '';
  let str = String(raw);

  // `100.00` → `100`
  str = str.replace(/(\d+)\.0+(?!\d)/g, '$1');

  // `25FS` → `25 FS` (регистронезависимо)
  str = str.replace(/(\d)\s*(FS)\b/gi, '$1 $2');

  // `100 ₱` → `₱ 100`
  if (currencySymbol) {
    let pattern = currencyAfterAmountPatterns.get(currencySymbol);
    if (!pattern) {
      const sym = escapeRegExp(currencySymbol);
      pattern = new RegExp(`(\\d[\\d.,]*)\\s*${sym}`, 'g');
      currencyAfterAmountPatterns.set(currencySymbol, pattern);
    }
    str = str.replace(pattern, `${currencySymbol} $1`);
  }

  return str;
};
